// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { CHANGE_VEHICLE_CLICKED } from '@makemydeal/dr-activities-change-vehicle';
import { actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';

// interfaces/types
import { type DashMiddleware, type DashNext, type DashStore, bootstrapActionTypes } from '@makemydeal/dr-shared-store';

// action types
import { OFFER_SAVE_FAILURE, OFFER_SAVE_SUCCESS } from '../actionTypes/offerActionTypes';

// selectors
import { getDealerships } from '../selectors/dealershipsSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as quoteSelectors from '../selectors/quoteSelectors';
import * as vehicleSelectors from '../selectors/vehicleSelectors';

// utils
import { createStoreScopeState } from '../utils/storeScopeState';
import { buildUsabilityContext } from '../utils/usabilityContext';
import { trackQuoteFirstSave, trackQuoteModeEntered, trackQuoteVehicleConditionChanged } from '../utils/quoteModeTelemetry';

const QUOTE_BASELINE_SCOPE = '__quoteModeBaseline';

const quoteBaselineByScope = createStoreScopeState<{ initialVehicleCondition: string | undefined }>(() => ({
    initialVehicleCondition: undefined
}));

const getQuoteBaseline = (store: DashStore) => quoteBaselineByScope(store, QUOTE_BASELINE_SCOPE);

const handleModeEntered = (store: DashStore): void => {
    const state = store.getState();

    if (!quoteSelectors.getQuoteMode(state)) {
        return;
    }

    const initialVehicleCondition = vehicleSelectors.getVehicleConditionLower(state);
    getQuoteBaseline(store).initialVehicleCondition = initialVehicleCondition;

    const hasDealXgRecord = !offerReduxSelectors.getIsDealXgRecordMissed(state);

    trackQuoteModeEntered(
        {
            entrySource: hasDealXgRecord ? 'initDealXg' : 'initOrg',
            hasDealXgRecord,
            quoteName: quoteSelectors.getQuoteName(state),
            initialVehicleCondition,
            hasVehicle: vehicleSelectors.getHasVehicle(state),
            dealershipCount: getDealerships(state).length
        },
        buildUsabilityContext(state)
    );
};

const handleFirstSave = (store: DashStore, action: AnyFSA): void => {
    const state = store.getState();
    const meta = action.meta || {};
    const isFailure = action.type === OFFER_SAVE_FAILURE;
    const saveStartTime = meta.saveStartTime;

    trackQuoteFirstSave(
        {
            outcome: isFailure ? 'failure' : 'success',
            isDraft: Boolean(meta.isDraft),
            source: meta.source,
            hasVehicle: vehicleSelectors.getHasVehicle(state),
            hasCustomer: !offerReduxSelectors.getIsShopperInfoMissed(state),
            durationMs: typeof saveStartTime === 'number' ? performance.now() - saveStartTime : undefined,
            errorMessage: isFailure ? action.payload?.message : undefined,
            errorStatus: isFailure ? action.payload?.status : undefined
        },
        buildUsabilityContext(state)
    );
};

const handleVehicleConditionChange = (
    store: DashStore,
    action: AnyFSA,
    fromCondition: string,
    toCondition: string,
    hadVehicleBefore: boolean
): void => {
    const state = store.getState();

    if (!quoteSelectors.getQuoteMode(state) || !fromCondition) {
        return;
    }

    const { initialVehicleCondition } = getQuoteBaseline(store);

    trackQuoteVehicleConditionChanged(
        {
            fromCondition,
            toCondition,
            changedFromDefault: initialVehicleCondition !== undefined && fromCondition === initialVehicleCondition,
            hadVehicleBefore,
            triggerType: action.type,
            isPrimaryScope: scopedStateSelectors.isCurrentScopePrimary(state as any),
            scopeId: scopedStateSelectors.getCurrentScopeId(state as any)
        },
        buildUsabilityContext(state)
    );
};

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    switch (action?.type) {
        case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
            const result = next(action);
            handleModeEntered(store);
            return result;
        }
        case OFFER_SAVE_SUCCESS:
        case OFFER_SAVE_FAILURE: {
            const isFirstQuoteSave = quoteSelectors.getIsQuoteModeWithoutDealXgRecord(store.getState());
            const result = next(action);

            if (isFirstQuoteSave) {
                handleFirstSave(store, action);
            }

            return result;
        }
        case CHANGE_VEHICLE_CLICKED:
        case offerReduxActionTypes.UPDATED_VEHICLE_INFO_DETAILS: {
            if (action.meta?.addNewScenario) {
                return next(action);
            }
            const preState = store.getState();
            const fromCondition = vehicleSelectors.getVehicleConditionLower(preState);
            const hadVehicleBefore = vehicleSelectors.getHasVehicle(preState);

            const result = next(action);

            const toCondition = vehicleSelectors.getVehicleConditionLower(store.getState());

            if (toCondition !== fromCondition) {
                handleVehicleConditionChange(store, action, fromCondition, toCondition, hadVehicleBefore);
            }

            return result;
        }
        default: {
            return next(action);
        }
    }
};
